import { useCallback, useEffect, useState } from "react";
import useNotificationMessageService from "../../../hooks/useNotificationMessageService";
import { Pagination } from "../../../types/PaginiationResult";
import { ApiTableState } from "../../../types/ApiTable/ApiTableState";
import TableActionButton from "../../../@ui/TableActionButton";
import { TableActionButtonDelete } from "../../../@ui/TableActionButtonDelete";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Notification,
  NotificationCreateRequest,
  NotificationUpdateRequest,
} from "../../../types/Notification";
import ApiTable from "../../../@ui/ApiTable/ApiTable";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import NotificationFormModal from "../../modal/Notification/NotificationFormModal";
import DOMPurify from "dompurify";
import DeleteModal from "../../modal/General/DeleteModal";

enum Modal {
  Create,
  Update,
  Delete,
}

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
`;

const TableMessageContent = styled.div`
  p {
    margin: 0;
  }
`;

const Notifications = () => {
  const notificationService = useNotificationMessageService();

  const [modalData, setModalData] = useState<{
    modal: Modal | null;
    notification: Notification | null;
    apiErrorMessage?: string;
  }>({
    modal: null,
    notification: null,
    apiErrorMessage: undefined,
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Pagination<Notification>>({
    records: [],
    totalPages: 1,
    page: 1,
  });

  const [requestData, setRequestData] = useState<ApiTableState>({
    pagination: { page: 1, pageSize: 10 },
  });

  useEffect(() => {
    updateTable(
      requestData.pagination?.page ?? 1,
      requestData.pagination?.pageSize ?? 10
    );
  }, [requestData, notificationService]);

  const updateTable = (page: number, pageSize: number) => {
    setLoading(true);
    // TODO: Replace with actual notificationService call
    notificationService.getNotifications(page, pageSize).then((result) => {
      setData(result);
      setLoading(false);
    });
  };

  const cols = [
    {
      field: "NotificationDate",
      label: "Date",
      renderContents: (value: Notification) => (
        <>{value.notificationDate.toFormat("dd/LL/yyyy HH:mm:ss")}</>
      ),
    },
    {
      field: "Message",
      label: "Message",
      renderContents: (value: Notification) => (
        <TableMessageContent
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(value.message!),
          }}
        />
      ),
    },
    {
      field: "CreatedBy",
      label: "Created By",
      renderContents: (value: Notification) => <>{value.createdBy?.username}</>,
    },
    {
      style: { width: "1%" },
      renderContents: (value: Notification) => (
        <>
          <TableActionButton
            onClick={() => {
              setModalData({ modal: Modal.Update, notification: value });
            }}
            title="Update"
          >
            Update
          </TableActionButton>
        </>
      ),
    },
    {
      style: { width: "1%" },
      renderContents: (value: Notification) => (
        <>
          <TableActionButtonDelete
            onClick={() => {
              setModalData({ modal: Modal.Delete, notification: value });
            }}
            title="Delete"
          >
            Delete
          </TableActionButtonDelete>
        </>
      ),
    },
  ] as ApiTableColumn[];

  const controls = () => {
    return (
      <div className="w-100 d-flex justify-content-start h-100 align-items-end">
        <TableActionButton
          onClick={() =>
            setModalData({ modal: Modal.Create, notification: null })
          }
          title="Create"
        >
          <StyledIcon icon={faPlus} />
          Create
        </TableActionButton>
      </div>
    );
  };

  const onHide = () => setModalData({ modal: null, notification: null });

  const onDelete = useCallback(
    (id: string) => {
      notificationService.deleteNotification(id).then(() => {
        updateTable(
          requestData.pagination?.page ?? 1,
          requestData.pagination?.pageSize ?? 10
        );
      });
    },
    [requestData]
  );

  const onSubmit = (
    values: NotificationCreateRequest | NotificationUpdateRequest
  ) => {
    if (modalData.modal === Modal.Create) {
      // TODO: Replace with notificationService.addNotification
      notificationService
        .addNotification(values)
        .then(() => {
          updateTable(
            requestData.pagination?.page ?? 1,
            requestData.pagination?.pageSize ?? 10
          );
          setModalData({
            modal: null,
            notification: null,
            apiErrorMessage: undefined,
          });
        })
        .catch((error) => {
          setModalData((prev) => ({
            ...prev,
            apiErrorMessage: error.response?.data,
          }));
        });
    } else if (modalData.modal === Modal.Update) {
      notificationService
        .updateNotification(values as any) // Replace with actual update request type
        .then(() => {
          updateTable(
            requestData.pagination?.page ?? 1,
            requestData.pagination?.pageSize ?? 10
          );
          setModalData({
            modal: null,
            notification: null,
            apiErrorMessage: undefined,
          });
        })
        .catch((error) => {
          setModalData((prev) => ({
            ...prev,
            apiErrorMessage: error.response?.data,
          }));
        });
    }
  };

  return (
    <>
      <h4>Notifications</h4>
      <ApiTable
        loading={loading}
        state={requestData}
        pageOptions={[10, 20, 50]}
        data={data}
        columns={cols}
        controls={controls()}
        onChange={(state) => setRequestData(state)}
      />
      <DeleteModal
        show={modalData.modal === Modal.Delete}
        id={modalData.notification?.notificationId ?? ""}
        onDelete={onDelete}
        onHide={onHide}
        label="Notification"
      />
      <NotificationFormModal
        show={
          modalData.modal === Modal.Create || modalData.modal === Modal.Update
        }
        onClose={onHide}
        notification={modalData.notification}
        onSubmit={onSubmit}
        apiErrorMessage={modalData.apiErrorMessage}
      />
    </>
  );
};

export default Notifications;
